import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  Card,
  Table,
  Button,
  Select,
  Row,
  Col,
  Modal,
  Checkbox,
  message,
  InputNumber,
  Input,
  Tooltip,
  DatePicker,
} from 'antd';
import {
  PlusOutlined,
  SaveOutlined,
  SettingOutlined,
  PrinterOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import ResizableSplitPane from '../common/ResizableSplitPane';
import { MakeupService } from '../../services/makeupService';
import { printStudentMakeupSheetPDF, printAllStudentMakeupSheets, PrintStudent } from '../../services/printService';
import type { HocBu, AttendanceViolation } from '../../../../shared/types/database';
import './StudentMakeupManagementPage.css';
import { buildAPIURL } from '../../config/apiConfig';

const apiBase = buildAPIURL(''); // base prefix like /api/v1 or full URL
// Fee constants
const BASE_TH_FEE = 35000;
const TH_WITH_CHEMICAL_FEE = 55000;
const BASE_LT_FEE = 55000;

interface Course {
  KhoaHocID: string;
  TenKhoaHoc: string;
}

interface Subject {
  SubjectId?: string;
  SubjectID?: string;
  MonHocID?: string;
  TenMonHoc?: string;
  TenMH?: string;
}

interface ClassSection {
  MaLHP: string;      // Mã lớp học phần (primary key)
  TenLHP: string;     // Tên lớp học phần
  IDCode: string;     // ID Code
  MaLop: string;      // Mã lớp
  MaMonHoc: string;   // Mã môn học
  KhoaHocID: string;  // Khóa học ID
  TenKhoaHoc: string; // Tên khóa học
  TenMonHoc: string;  // Tên môn học
  HocKy: number;      // Học kỳ
  IsActive: number;   // Trạng thái active
}

interface Student {
  MaHS: string;
  HoTen: string;
  NgaySinh: string;
  TenLop: string;
  LopSinhHoat?: string;
}

interface MakeupRow extends Student {
  id: string; // Unique identifier for each row
  MonHocID?: string;
  LopHocPhanID?: string;
  SoTietLT: number;
  SoTietTH: number;
  CoHoaChat: boolean;
  ThanhTien: number;
  DaDongTien: boolean;
  TrangThai: string;
  GhiChu?: string;
  TenBaiGiang?: string;
  GiangVien?: string;
  BuoiSo?: number;
  BuoiGiang?: number;
  NgayGiang?: string;
  HinhThuc: 'Học bù' | 'Học lại';
  ViolationDetails?: AttendanceViolation[]; // Store violation details for this row
}

const StudentMakeupManagementPage: React.FC = () => {
  // State declarations
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [classSections, setClassSections] = useState<ClassSection[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudentKeys, setSelectedStudentKeys] = useState<React.Key[]>([]);
  const [makeupList, setMakeupList] = useState<MakeupRow[]>([]);
  const [savedMakeupList, setSavedMakeupList] = useState<MakeupRow[]>([]);

  // Modal states
  const [attendanceDetail, setAttendanceDetail] = useState<AttendanceViolation[]>([]);
  const [attendanceStudent, setAttendanceStudent] = useState<Student | null>(null);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [selectedAttendanceKeys, setSelectedAttendanceKeys] = useState<number[]>([]);

  // Filter states
  const [selectedCourse, setSelectedCourse] = useState<string>();
  const [selectedSubject, setSelectedSubject] = useState<string>();
  const [selectedClassSection, setSelectedClassSection] = useState<string>();
  const [studentSearchText, setStudentSearchText] = useState('');

  // Add states for saved makeup list from API
  const [selectedDate, setSelectedDate] = useState(dayjs()); // Date filter for saved list
  const [makeups, setMakeups] = useState<any[]>([]); // Data from HocBu table
  const [loadingMakeups, setLoadingMakeups] = useState(false);

  // Add states for deleted log modal
  const [deletedLogModalOpen, setDeletedLogModalOpen] = useState(false);
  const [deletedLogList, setDeletedLogList] = useState<any[]>([]);
  const [deletedLogLoading, setDeletedLogLoading] = useState(false);

  // Initialize username and signature on mount
  useEffect(() => {
    // Logic to get username and signature can be restored here if needed
    loadCourses();
    loadSavedMakeups(); // Load initial data
  }, []);

  // Auto reload saved makeups when date changes
  useEffect(() => {
    loadSavedMakeups();
  }, [selectedDate]);

  // Load initial data
  const loadCourses = () => {
    setLoading(true);
    fetch(`${apiBase}/courses`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
      }
    })
      .then(r => r.json())
      .then(response => {
        console.log('[DEBUG] API /courses response:', response);

        // Kiểm tra nếu response có format {success: true, data: [...]}
        const coursesArray = response.success && Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
        console.log('[DEBUG] Courses array to set:', coursesArray);
        setCourses(coursesArray);
      })
      .catch(error => {
        console.error('Error loading courses:', error);
        message.error('Không thể tải danh sách khóa học');
      })
      .finally(() => setLoading(false));
  };

  // Fetch students for selected class section
  const fetchStudents = async () => {
    if (!selectedClassSection) {
      message.warning('Vui lòng chọn lớp học phần');
      return;
    }

    setLoading(true);
    try {
      // Sử dụng MakeupService.getStudentsByClassSection như file cũ
      // Endpoint này sẽ gọi /api/v1/hocbu/students với params malophp
      const studentData = await MakeupService.getStudentsByClassSection({
        malophp: selectedClassSection.trim()
      });

      const rawData = Array.isArray(studentData) ? studentData : [];
      const formattedData = rawData.map((item: any) => {
        let formattedDate = '';
        try {
          if (item.NgaySinh && typeof item.NgaySinh === 'string') {
            if (item.NgaySinh.includes('/')) {
              formattedDate = item.NgaySinh;
            } else {
              const date = new Date(item.NgaySinh);
              if (!isNaN(date.getTime())) {
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const year = date.getFullYear();
                formattedDate = `${day}/${month}/${year}`;
              }
            }
          }
        } catch (err) {
          console.error('Error formatting birth date:', err);
        }

        return {
          MaHS: item.MaHS,
          HoTen: item.HoTen,
          NgaySinh: formattedDate,
          TenLop: item.TenLop,
          LopSinhHoat: item.TenLop
        };
      });

      // Sắp xếp sinh viên theo Mã SV tăng dần
      const sortedStudentArray = formattedData.sort((a: Student, b: Student) => {
        const maA = (a.MaHS || '').toString();
        const maB = (b.MaHS || '').toString();
        return maA.localeCompare(maB);
      });

      setStudents(sortedStudentArray);
      setSelectedStudentKeys([]);

      if (sortedStudentArray.length === 0) {
        message.warning('Không tìm thấy sinh viên nào trong lớp học phần này');
      } else {
        message.success(`Đã tải ${sortedStudentArray.length} sinh viên`);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      message.error(`Không thể lấy danh sách sinh viên: ${error instanceof Error ? error.message : 'Lỗi không xác định'}`);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle class section change
  const handleClassSectionChange = (value: string) => {
    setSelectedClassSection(value);
    setStudents([]);
  };

  // Add students to makeup list
  const handleAddToMakeupList = () => {
    if (selectedStudentKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một sinh viên');
      return;
    }

    if (!selectedSubject || !selectedClassSection) {
      message.error('Vui lòng chọn đầy đủ môn học và lớp học phần trước khi thêm sinh viên');
      return;
    }

    const selectedStudents = students.filter(s => selectedStudentKeys.includes(s.MaHS));
    const newItems: MakeupRow[] = selectedStudents.map(student => {
      // Tạo ID duy nhất với timestamp và random string
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substr(2, 9);
      const uniqueId = `${student.MaHS}-${timestamp}-${randomStr}`;

      return {
        ...student,
        id: uniqueId,
        MonHocID: selectedSubject,
        LopHocPhanID: selectedClassSection,
        SoTietLT: 0,
        SoTietTH: 0,
        CoHoaChat: false,
        ThanhTien: 0,
        DaDongTien: false,
        TrangThai: 'Chưa đóng tiền',
        GhiChu: '',
        BuoiSo: 1,
        HinhThuc: 'Học bù' as 'Học bù' | 'Học lại',
        TenBaiGiang: '',
        GiangVien: ''
      };
    });

    setMakeupList(prev => [...prev, ...newItems]);
    setSelectedStudentKeys([]);
    message.success(`Đã thêm ${newItems.length} sinh viên vào danh sách học bù`);
  };

  // Calculate makeup row fee
  const calculateFee = useCallback((soTietLT: number, soTietTH: number, coHoaChat: boolean, hinhThuc: string) => {
    let thanhTien = 0;
    if (hinhThuc === 'Học lại') {
      thanhTien += soTietLT * BASE_LT_FEE;
      thanhTien += soTietTH * (coHoaChat ? TH_WITH_CHEMICAL_FEE : BASE_TH_FEE);
    } else { // Học bù
      thanhTien += soTietLT * BASE_LT_FEE;
      thanhTien += soTietTH * (coHoaChat ? TH_WITH_CHEMICAL_FEE : BASE_TH_FEE);
    }
    return thanhTien;
  }, []);

  // Handle makeup row changes
  const handleMakeupRowChange = useCallback((id: string, field: string, value: any) => {
    setMakeupList(prev => prev.map(row => {
      if (row.id === id) {
        const newRow = { ...row, [field]: value };

        // Recalculate fee when relevant fields change
        if (['SoTietLT', 'SoTietTH', 'CoHoaChat', 'HinhThuc'].includes(field)) {
          const soTietLT = field === 'SoTietLT' ? value : row.SoTietLT;
          const soTietTH = field === 'SoTietTH' ? value : row.SoTietTH;
          const coHoaChat = field === 'CoHoaChat' ? value : row.CoHoaChat;
          const hinhThuc = field === 'HinhThuc' ? value : row.HinhThuc;

          newRow.ThanhTien = calculateFee(soTietLT, soTietTH, coHoaChat, hinhThuc);
        }

        return newRow;
      }
      return row;
    }));
  }, [calculateFee]);

  // Save makeup list
  const handleSaveList = async () => {
    if (makeupList.length === 0) {
      message.warning('Danh sách học bù trống');
      return;
    }

    // Validate required fields
    if (!selectedSubject || !selectedClassSection) {
      message.error('Vui lòng chọn đầy đủ môn học và lớp học phần');
      return;
    }

    // Prepare data with required fields
    const preparedData = makeupList.map(item => ({
      MaHS: item.MaHS,
      HoTen: item.HoTen,
      MonHocID: selectedSubject, // Lấy từ dropdown selection
      LopHocPhanID: selectedClassSection, // Lấy từ dropdown selection
      BuoiSo: item.BuoiSo || 1,
      BuoiGiang: item.BuoiGiang || 1,
      SoTietLT: item.SoTietLT || 0,
      SoTietTH: item.SoTietTH || 0,
      CoHoaChat: item.CoHoaChat || false,
      ThanhTien: item.ThanhTien || 0,
      HinhThuc: item.HinhThuc || 'Học bù',
      GhiChu: item.GhiChu || '',
      TenBaiGiang: item.TenBaiGiang || '',
      GiangVien: item.GiangVien || '',
      NgayGiang: item.NgayGiang || null
    }));

    console.log('🔍 [DEBUG] Saving makeup list:', {
      totalItems: preparedData.length,
      selectedSubject,
      selectedClassSection,
      sampleItem: preparedData[0]
    });

    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/hocbu/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        },
        body: JSON.stringify({ items: preparedData })
      });

      const responseData = await response.json();
      console.log('🔍 [DEBUG] Server response:', responseData);

      if (response.ok) {
        // Kiểm tra kỹ hơn response từ API để hiển thị thông báo chính xác
        const { successful = 0, skipped = 0, failed = 0, results = [] } = responseData.data || {};

        if (responseData.success && successful > 0) {
          // Có ít nhất 1 bản ghi lưu thành công
          if (skipped > 0 || failed > 0) {
            // Thành công một phần
            message.warning(`Đã xử lý ${makeupList.length} bản ghi: ${successful} thành công, ${skipped} bỏ qua (trùng lặp), ${failed} thất bại`);
          } else {
            // Hoàn toàn thành công
            message.success(`Lưu danh sách học bù thành công (${successful} bản ghi)`);
          }
          setMakeupList([]);
          loadSavedMakeups();
        } else if (skipped > 0 && successful === 0 && failed === 0) {
          // Tất cả đều bị skip (trùng lặp)
          const duplicateStudents = results
            .filter((r: any) => r.status === 'skipped')
            .map((r: any) => r.MaHS)
            .slice(0, 3)
            .join(', ');
          message.warning(`Không có bản ghi mới được lưu - tất cả ${skipped} bản ghi đã tồn tại trong CSDL (${duplicateStudents}${results.length > 3 ? '...' : ''})`);
          // Không xóa makeupList để user có thể sửa hoặc xóa bản ghi trùng
        } else if (failed > 0) {
          // Có lỗi xảy ra
          message.error(`Lỗi khi lưu: ${failed} bản ghi thất bại, ${successful} thành công, ${skipped} bỏ qua`);
          if (successful > 0) {
            // Refresh nếu có một số thành công
            loadSavedMakeups();
          }
        } else {
          // Trường hợp khác (không có data)
          message.info(responseData.message || 'Đã xử lý xong');
        }
      } else {
        console.error('❌ [ERROR] Server returned error:', responseData);
        throw new Error(responseData.message || 'Failed to save');
      }
    } catch (error) {
      console.error('❌ [ERROR] Error saving makeup list:', error);
      message.error(`Không thể lưu danh sách học bù: ${error instanceof Error ? error.message : 'Lỗi không xác định'}`);
    } finally {
      setLoading(false);
    }
  };

  // Load saved makeups
  const loadSavedMakeups = async () => {
    setLoadingMakeups(true);
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      console.log('🔍 [DEBUG] Loading saved makeups for date:', dateStr);

      const response = await fetch(`${apiBase}/hocbu?date=${dateStr}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });
      const data = await response.json();

      console.log('🔍 [DEBUG] Loaded makeups response:', {
        success: data.success,
        dataLength: data.data?.length || 0,
        totalFound: data.data?.length || 0
      });

      const makeupData = data?.data || [];
      setMakeups(makeupData);

      console.log('✅ [DEBUG] Final makeups set:', makeupData.length, 'items');
    } catch (error) {
      console.error('❌ [ERROR] Error loading saved makeups:', error);
      message.error('Không thể tải danh sách học bù đã lưu');
    } finally {
      setLoadingMakeups(false);
    }
  };

  // Delete makeup
  const handleDeleteMakeup = async (record: any) => {
    try {
      const response = await fetch(`${apiBase}/hocbu/${record.HocBuID}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        message.success('Xóa thành công');
        loadSavedMakeups();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting makeup:', error);
      message.error('Không thể xóa bản ghi học bù');
    }
  };

  // Print functions
  const handlePrint = (record: MakeupRow) => {
    const printStudent: PrintStudent = {
      MaHS: record.MaHS,
      HoTen: record.HoTen,
      LopSinhHoat: record.LopSinhHoat || record.TenLop,
      SoTietLT: record.SoTietLT,
      SoTietTH: record.SoTietTH,
      CoHoaChat: record.CoHoaChat,
      ThanhTien: record.ThanhTien,
      TenBaiGiang: record.TenBaiGiang,
      GiangVien: record.GiangVien,
      BuoiSo: record.BuoiSo,
      BuoiGiang: record.BuoiGiang,
      HinhThuc: record.HinhThuc
    };

    // Get subject and class section names for display
    const subjectName = subjects.find(s =>
      String(s.SubjectId || s.SubjectID || s.MonHocID) === selectedSubject
    )?.TenMonHoc || subjects.find(s =>
      String(s.SubjectId || s.SubjectID || s.MonHocID) === selectedSubject
    )?.TenMH || '';

    const classSectionName = classSections.find(cs =>
      cs.MaLHP.trim() === selectedClassSection
    )?.TenLHP || '';

    printStudentMakeupSheetPDF(printStudent, subjectName, classSectionName);
  };

  const handlePrintAll = () => {
    if (makeupList.length === 0) {
      message.warning('Danh sách học bù trống');
      return;
    }

    const printStudents: PrintStudent[] = makeupList.map(record => ({
      MaHS: record.MaHS,
      HoTen: record.HoTen,
      LopSinhHoat: record.LopSinhHoat || record.TenLop,
      SoTietLT: record.SoTietLT,
      SoTietTH: record.SoTietTH,
      CoHoaChat: record.CoHoaChat,
      ThanhTien: record.ThanhTien,
      TenBaiGiang: record.TenBaiGiang,
      GiangVien: record.GiangVien,
      BuoiSo: record.BuoiSo,
      BuoiGiang: record.BuoiGiang,
      HinhThuc: record.HinhThuc
    }));

    // Get subject and class section names for display
    const subjectName = subjects.find(s =>
      String(s.SubjectId || s.SubjectID || s.MonHocID) === selectedSubject
    )?.TenMonHoc || subjects.find(s =>
      String(s.SubjectId || s.SubjectID || s.MonHocID) === selectedSubject
    )?.TenMH || '';

    const classSectionName = classSections.find(cs =>
      cs.MaLHP.trim() === selectedClassSection
    )?.TenLHP || '';

    printAllStudentMakeupSheets(printStudents, subjectName, classSectionName);
  };

  // Student table columns
  const studentColumns = [
    { title: 'Mã SV', dataIndex: 'MaHS', width: 100 },
    { title: 'Họ và tên', dataIndex: 'HoTen', width: 180 },
    {
      title: 'Ngày sinh', dataIndex: 'NgaySinh', width: 120,
      render: (val: string) => {
        if (!val) return '';
        try {
          // Check if it's already in DD/MM/YYYY format
          if (typeof val === 'string' && val.includes('/')) {
            return val;
          }
          const date = new Date(val);
          if (isNaN(date.getTime())) return '';
          return date.toLocaleDateString('vi-VN');
        } catch (error) {
          return '';
        }
      }
    },
    { title: 'Lớp', dataIndex: 'TenLop', width: 120 },
    {
      title: 'Chi tiết điểm danh',
      key: 'attendance',
      width: 150,
      render: (_: any, record: Student) => (
        <Button
          type="link"
          onClick={(e) => {
            e.stopPropagation();
            handleShowAttendanceDetail(record);
          }}
        >
          Xem vi phạm
        </Button>
      ),
    },
  ];

  const handleCourseChange = (value: string) => {
    setSelectedCourse(value);
    setSelectedSubject(undefined);
    setSelectedClassSection(undefined);
    setSubjects([]);
    setClassSections([]);
    setStudents([]);

    if (value) {
      setLoading(true);
      console.log('🔍 Loading subjects for course:', value);
      fetch(`${apiBase}/subjects/by-khoahoc/${encodeURIComponent(value)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      })
        .then(r => r.json())
        .then(response => {
          console.log('[DEBUG] API /subjects/by-khoahoc response:', response);

          // Kiểm tra nếu response có format {success: true, data: [...]} hoặc {data: [...]}
          const subjectsArray = response.success && Array.isArray(response.data) ? response.data :
            (response.data && Array.isArray(response.data) ? response.data :
              (Array.isArray(response) ? response : []));
          console.log('📚 Subjects loaded:', subjectsArray.length);
          setSubjects(subjectsArray);
        })
        .catch(err => {
          console.error('Error loading subjects:', err);
          message.error('Không thể tải danh sách môn học');
          setSubjects([]);
        })
        .finally(() => setLoading(false));
    }
  };

  const handleSubjectChange = (value: string) => {
    setSelectedSubject(value);
    setSelectedClassSection(undefined);
    setClassSections([]);
    setStudents([]);

    if (value && selectedCourse) {
      setLoading(true);
      console.log('🔍 Loading class sections for subject:', value);

      fetch(`${apiBase}/lophocphan/by-monhoc/${encodeURIComponent(value)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      })
        .then(r => r.json())
        .then(response => {
          console.log('[DEBUG] API /lophocphan/by-monhoc response:', response);

          // Kiểm tra nếu response có format {success: true, data: [...]} hoặc {data: [...]}
          const classSectionsArray = response.success && Array.isArray(response.data) ? response.data :
            (response.data && Array.isArray(response.data) ? response.data :
              (Array.isArray(response) ? response : []));
          console.log('✅ Class sections loaded:', classSectionsArray.length);
          setClassSections(classSectionsArray);
        })
        .catch(error => {
          console.error('Error loading class sections:', error);
          message.error('Không thể tải danh sách lớp học phần');
          setClassSections([]);
        })
        .finally(() => setLoading(false));
    }
  };

  const handleShowAttendanceDetail = async (student: Student) => {
    if (!selectedCourse || !selectedSubject) {
      message.info('Vui lòng chọn khóa học và môn học!');
      return;
    }

    // Check if class section is selected - required for API
    if (!selectedClassSection) {
      message.info('Vui lòng chọn lớp học phần!');
      return;
    }

    setAttendanceStudent(student);
    setAttendanceDetail([]);
    setSelectedAttendanceKeys([]);
    setAttendanceModalOpen(true);
    setLoading(true);

    try {
      // Gọi API lấy dữ liệu vi phạm thực từ stored procedure GetSVViPham
      // SP cần 4 tham số:
      // @MaNganh = KhoaHocID (từ dropdown Khóa học)
      // @MaMonHoc = MonHocID (từ dropdown Môn học) 
      // @MaLopHocPhan = LopHocPhanID (từ dropdown Lớp học phần)
      // @MaSinhVien = MaHS (từ sinh viên được click)
      const apiParams = {
        maNganh: selectedCourse,      // KhoaHocID từ dropdown Khóa học
        maMonHoc: selectedSubject,    // MonHocID từ dropdown Môn học
        maLopHocPhan: selectedClassSection, // LopHocPhanID từ dropdown Lớp học phần
        maSinhVien: student.MaHS      // MaHS từ sinh viên được click
      };

      console.log('🔍 Calling attendance violations API with params:');
      console.log('  - selectedCourse (KhoaHocID):', selectedCourse);
      console.log('  - selectedSubject (MonHocID):', selectedSubject);
      console.log('  - selectedClassSection (LopHocPhanID):', selectedClassSection);
      console.log('  - student.MaHS (MaSinhVien):', student.MaHS);
      console.log('  - apiParams:', apiParams);

      const queryString = new URLSearchParams(apiParams).toString();
      const fullUrl = `${apiBase}/hocbu/attendance-violations?${queryString}`;
      const response = await fetch(fullUrl);
      if (!response.ok) {
        // Try to get error details from response
        try {
          const errorData = await response.json();
          throw new Error(`API Error ${response.status}: ${errorData.error || response.statusText}`);
        } catch (parseError) {
          console.log('❌ Cannot parse error response, using status text');
          throw new Error(`Lỗi API: ${response.status} ${response.statusText}`);
        }
      }

      const result = await response.json();
      // Handle new API format {success: true, data: [...]} or old format [...]
      let attendanceData = [];
      if (result.success && Array.isArray(result.data)) {
        attendanceData = result.data;
      } else if (Array.isArray(result)) {
        attendanceData = result;
      }

      setAttendanceDetail(attendanceData);
      if (attendanceData.length === 0) {
        message.info('Sinh viên này không có vi phạm điểm danh nào.');
      } else {
      }
    } catch (error) {
      console.error('Lỗi khi lấy dữ liệu vi phạm:', error);
      const errorMessage = error instanceof Error ? error.message : 'Lỗi không xác định';
      message.error('Không thể lấy dữ liệu vi phạm: ' + errorMessage);

      // Fallback: Hiển thị dữ liệu mô phỏng khi có lỗi kết nối
      console.log('Fallback to mock data due to API error');
      const fallbackData = [
        {
          BuoiSo: 1,
          tenbg: 'Bài giảng 1 - Giới thiệu môn học',
          ngaygiang: '2025-01-15',
          tengv: 'Nguyễn Văn A',
          sotietLT: 2,
          sotietTH: 0,
          sophutmuon: 0,
          sobuoivang: 1,
          solanbotiet: 0,
          isTHHoaChat: 'false'
        },
        {
          BuoiSo: 3,
          tenbg: 'Bài giảng 3 - Thực hành lab',
          ngaygiang: '2025-01-22',
          tengv: 'Nguyễn Văn A',
          sotietLT: 0,
          sotietTH: 2,
          sophutmuon: 0,
          sobuoivang: 1,
          solanbotiet: 0,
          isTHHoaChat: 'true'
        }
      ];
      setAttendanceDetail(fallbackData);
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = students.filter(student =>
    student.HoTen.toLowerCase().includes(studentSearchText.toLowerCase())
  );

  const leftPanel = (
    <Card
      title={`Danh sách sinh viên (${filteredStudents.length})`}
      bordered={false}
      className="left-panel-card"
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      bodyStyle={{ flex: 1, overflowY: 'auto', padding: '16px' }}
      extra={
        <Button icon={<PlusOutlined />} onClick={handleAddToMakeupList} disabled={selectedStudentKeys.length === 0}>
          Thêm SV sang học bù
        </Button>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Tìm theo tên sinh viên"
          onChange={(e) => setStudentSearchText(e.target.value)}
          onSearch={(value) => setStudentSearchText(value)}
          style={{ width: '100%' }}
        />
      </div>
      <Table
        rowKey="MaHS"
        dataSource={filteredStudents}
        columns={studentColumns}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedStudentKeys,
          onChange: (keys) => setSelectedStudentKeys(keys),
        }}
        onRow={(record) => ({
          onClick: () => {
            if (selectedSubject && selectedClassSection) {
              handleShowAttendanceDetail(record);
            } else {
              message.warning('Vui lòng chọn môn học và lớp học phần trước');
            }
          },
          style: { cursor: 'pointer' }
        })}
        pagination={{ pageSize: 10, size: 'small' }}
        size="small"
        loading={loading}
        scroll={{ y: 400 }}
      />
    </Card>
  );

  const rightPanel = (
    <Card
      title={`Danh sách sinh viên học bù (${makeupList.length})`}
      bordered={false}
      className="right-panel-card"
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      bodyStyle={{ flex: 1, overflowY: 'auto', padding: '16px' }}
      extra={
        <div>
          <Button
            type="default"
            icon={<SaveOutlined />}
            style={{ marginRight: 8 }}
            disabled={makeupList.length === 0}
            onClick={handleSaveList}
            loading={loading}
          >
            Lưu danh sách
          </Button>

          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrintAll}
            style={{ marginRight: 8 }}
            disabled={makeupList.length === 0}
          >
            In tất cả phiếu học bù
          </Button>
        </div>
      }
    >
      <Table
        rowKey="id"
        dataSource={makeupList}
        columns={[
          { title: 'Mã SV', dataIndex: 'MaHS', width: 100 },
          { title: 'Họ và tên', dataIndex: 'HoTen', width: 180 },
          { title: 'Lớp sinh hoạt', dataIndex: 'LopSinhHoat', width: 120 },
          {
            title: 'Buổi số',
            dataIndex: 'BuoiSo',
            width: 80,
            render: (text, record) => (
              <InputNumber
                min={1}
                value={text}
                onChange={(value) => handleMakeupRowChange(record.id!, 'BuoiSo', value)}
                style={{ width: '100%' }}
              />
            ),
          },
          {
            title: 'Ngày giảng',
            dataIndex: 'NgayGiang',
            width: 120,
            render: (text, record) => (
              <input
                type="date"
                value={text ? (typeof text === 'string' && text.includes('-') ? text.split('T')[0] : new Date(text).toISOString().split('T')[0]) : ''}
                onChange={(e) => handleMakeupRowChange(record.id!, 'NgayGiang', e.target.value)}
                style={{ width: '100%', padding: '4px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
              />
            ),
          },
          {
            title: 'Số tiết LT',
            dataIndex: 'SoTietLT',
            width: 100,
            render: (text, record) => (
              <InputNumber
                min={0}
                value={text}
                onChange={(value) => handleMakeupRowChange(record.id!, 'SoTietLT', value)}
              />
            ),
          },
          {
            title: 'Số tiết TH',
            dataIndex: 'SoTietTH',
            width: 100,
            render: (text, record) => (
              <InputNumber
                min={0}
                value={text}
                onChange={(value) => handleMakeupRowChange(record.id!, 'SoTietTH', value)}
              />
            ),
          },
          {
            title: 'Có hóa chất',
            dataIndex: 'CoHoaChat',
            width: 100,
            render: (text, record) => (
              <Checkbox
                checked={text}
                onChange={(e) => handleMakeupRowChange(record.id!, 'CoHoaChat', e.target.checked)}
              />
            ),
          },
          {
            title: 'Hình thức',
            dataIndex: 'HinhThuc',
            width: 120,
            render: (text, record) => (
              <Select
                value={text}
                onChange={(value) => handleMakeupRowChange(record.id!, 'HinhThuc', value)}
                style={{ width: '100%' }}
              >
                <Select.Option value="Học bù">Học bù</Select.Option>
                <Select.Option value="Học lại">Học lại</Select.Option>
              </Select>
            ),
          },
          {
            title: 'Ghi chú',
            dataIndex: 'GhiChu',
            width: 150,
            render: (text, record) => (
              <Input
                value={text}
                onChange={(e) => handleMakeupRowChange(record.id!, 'GhiChu', e.target.value)}
                placeholder="Nhập ghi chú..."
                style={{ width: '100%' }}
              />
            ),
          },
          { title: 'Thành tiền', dataIndex: 'ThanhTien', width: 120, render: (val) => val ? val.toLocaleString('vi-VN') + 'đ' : '0đ' },
          { title: 'Trạng thái', dataIndex: 'TrangThai', width: 150 },
          {
            title: 'In phiếu',
            key: 'action',
            width: 80,
            fixed: 'right',
            render: (_, record) => (
              <Button icon={<PrinterOutlined />} onClick={() => handlePrint(record)} />
            ),
          },
          {
            title: '',
            key: 'delete',
            width: 50,
            fixed: 'right',
            render: (_, record) => (
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={() => setMakeupList(prev => prev.filter(row => row.id !== record.id))}
              />
            ),
          },
        ]}
        pagination={{ pageSize: 10, size: 'small' }}
        size="small"
        loading={loading}
        scroll={{ x: 'max-content', y: 400 }}
      />
    </Card>
  );

  // Helper functions for attendance statistics (from old system)
  const getAttendanceLateCount = useCallback(() => {
    return attendanceDetail.filter(detail => (detail.sophutmuon || 0) > 0).length;
  }, [attendanceDetail]);

  const getAttendanceAbsentCount = useCallback(() => {
    return attendanceDetail.filter(detail => (detail.sobuoivang || 0) > 0).length;
  }, [attendanceDetail]);

  const getAttendanceSkipCount = useCallback(() => {
    return attendanceDetail.reduce((total, detail) => total + (Number(detail.solanbotiet) || 0), 0);
  }, [attendanceDetail]);

  // Handle select all attendances function (from old system)
  const handleSelectAllAttendances = useCallback(() => {
    if (!attendanceDetail.length || !attendanceStudent) {
      message.info('Không có buổi học nào để chọn');
      return;
    }

    // Filter unselected sessions that are not already in makeup list
    const unselectedSessions = attendanceDetail.filter((row, index) => {
      if (selectedAttendanceKeys.includes(index)) {
        return false; // Already selected
      }

      // Check if already exists in makeup list (more flexible check)
      const isDuplicate = makeupList.some(item =>
        item.MaHS === attendanceStudent.MaHS &&
        item.BuoiSo === row.BuoiSo &&
        item.NgayGiang === row.ngaygiang &&
        item.TenBaiGiang === row.tenbg // Additional check
      );

      return !isDuplicate; // Only include if not duplicate
    });

    if (unselectedSessions.length === 0) {
      message.info('Tất cả các buổi đã được chọn hoặc đã có trong danh sách học bù');
      return;
    }

    // Add all unselected, non-duplicate sessions to makeup list
    const newItems: MakeupRow[] = unselectedSessions.map((row, idx) => {
      const soTietLT = row.sotietLT || 0;
      const soTietTH = row.sotietTH || 0;
      const coHoaChat = row.isTHHoaChat === 'true';

      let thanhTien = 0;
      if (coHoaChat) {
        thanhTien = soTietLT * BASE_LT_FEE + soTietTH * TH_WITH_CHEMICAL_FEE;
      } else {
        thanhTien = soTietLT * BASE_LT_FEE + soTietTH * BASE_TH_FEE;
      }

      // Create unique ID with timestamp and index to prevent duplicates
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substr(2, 9);
      const uniqueId = `${attendanceStudent.MaHS}-${row.BuoiSo}-${row.ngaygiang}-${timestamp}-${idx}-${randomId}`;

      return {
        ...attendanceStudent,
        id: uniqueId,
        MonHocID: selectedSubject,
        LopHocPhanID: selectedClassSection,
        SoTietLT: soTietLT,
        SoTietTH: soTietTH,
        CoHoaChat: coHoaChat,
        ThanhTien: thanhTien,
        DaDongTien: false,
        TrangThai: 'Chưa đóng tiền',
        GhiChu: '',
        TenBaiGiang: row.tenbg,
        GiangVien: row.tengv,
        BuoiSo: row.BuoiSo,
        NgayGiang: row.ngaygiang,
        HinhThuc: ((row.sobuoivang || 0) > 0 ? 'Học bù' : 'Học lại') as 'Học bù' | 'Học lại',
        ViolationDetails: [row]
      } as MakeupRow;
    });

    setMakeupList(prev => [...prev, ...newItems]);

    // Update selected keys to include all sessions
    const allIndices = attendanceDetail.map((_, index) => index);
    setSelectedAttendanceKeys(allIndices);

    message.success(`Đã thêm ${newItems.length} buổi vào danh sách học bù`);

    // Close modal after selecting all (like old system)
    setAttendanceModalOpen(false);
  }, [attendanceDetail, attendanceStudent, selectedAttendanceKeys, selectedSubject, selectedClassSection, makeupList]);

  // Handle attendance row select (from old system)
  const handleAttendanceSelect = useCallback((record: AttendanceViolation, index: number) => {
    if (!attendanceStudent) return;

    // Check if already selected
    if (selectedAttendanceKeys.includes(index)) {
      message.info('Buổi này đã được chọn!');
      return;
    }

    // Create unique key for this violation record with timestamp to prevent duplicates
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substr(2, 9);
    const violationKey = `${attendanceStudent.MaHS}-${record.BuoiSo}-${record.ngaygiang}-${timestamp}-${randomId}`;

    // Check if this exact violation already exists in makeup list (more flexible check)
    const isDuplicate = makeupList.some(item =>
      item.MaHS === attendanceStudent.MaHS &&
      item.BuoiSo === record.BuoiSo &&
      item.NgayGiang === record.ngaygiang &&
      item.TenBaiGiang === record.tenbg // Additional check to allow same session but different lessons
    );

    if (isDuplicate) {
      message.warning('Buổi học này đã có trong danh sách học bù!');
      return;
    }

    // Add to selected keys
    setSelectedAttendanceKeys(prev => [...prev, index]);

    // Calculate fee based on old system logic
    const soTietLT = record.sotietLT || 0;
    const soTietTH = record.sotietTH || 0;
    const coHoaChat = record.isTHHoaChat === 'true';

    let thanhTien = 0;
    if (coHoaChat) {
      thanhTien = soTietLT * BASE_LT_FEE + soTietTH * TH_WITH_CHEMICAL_FEE;
    } else {
      thanhTien = soTietLT * BASE_LT_FEE + soTietTH * BASE_TH_FEE;
    }

    // Create new makeup row exactly like old system
    const newMakeupRow: MakeupRow = {
      ...attendanceStudent,
      id: violationKey,
      MonHocID: selectedSubject,
      LopHocPhanID: selectedClassSection,
      SoTietLT: soTietLT,
      SoTietTH: soTietTH,
      CoHoaChat: coHoaChat,
      ThanhTien: thanhTien,
      DaDongTien: false,
      TrangThai: 'Chưa đóng tiền',
      GhiChu: '',
      TenBaiGiang: record.tenbg,
      GiangVien: record.tengv,
      BuoiSo: record.BuoiSo,
      BuoiGiang: record.BuoiGiang,
      NgayGiang: record.ngaygiang,
      HinhThuc: ((record.sobuoivang || 0) > 0 ? 'Học bù' : 'Học lại') as 'Học bù' | 'Học lại',
      ViolationDetails: [record]
    };

    // Add to makeup list
    setMakeupList(prev => [...prev, newMakeupRow]);
    message.success(`Đã thêm buổi ${record.BuoiSo} vào danh sách học bù`);
  }, [attendanceStudent, selectedAttendanceKeys, selectedSubject, selectedClassSection, makeupList]);

  return (
    <div className="page-makeup-management">
      <Card
        title="Quản lý học bù - Đào tạo"
        bordered={false}
        extra={<Button icon={<SettingOutlined />}>Cài đặt chữ ký</Button>}
      >
        <Row gutter={16} align="bottom">
          <Col span={7}>
            <label>Khóa học</label>
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              placeholder="Chọn khóa học"
              value={selectedCourse}
              options={courses.map(c => ({ value: c.KhoaHocID, label: c.TenKhoaHoc }))}
              onChange={handleCourseChange}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={7}>
            <label>Môn học</label>
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              placeholder="Chọn môn học"
              value={selectedSubject}
              options={subjects.map(s => ({
                value: String(s.SubjectId || s.SubjectID || s.MonHocID),
                label: `${s.SubjectId || s.SubjectID || s.MonHocID} - ${s.TenMonHoc || s.TenMH}`
              }))}
              onChange={handleSubjectChange}
              style={{ width: '100%' }}
              disabled={!selectedCourse}
            />
          </Col>
          <Col span={7}>
            <label>Lớp học phần</label>
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              placeholder="Chọn lớp học phần"
              value={selectedClassSection}
              options={classSections.map(cs => ({
                value: cs.MaLHP.trim(),
                label: `${cs.MaLHP.trim()} - ${cs.TenLHP}`
              }))}
              onChange={handleClassSectionChange}
              style={{ width: '100%' }}
              disabled={!selectedSubject}
            />
          </Col>
          <Col span={3}>
            <Button type="primary" onClick={fetchStudents} loading={loading} style={{ width: '100%' }}>
              Lấy danh sách
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Phần giữa: ResizableSplitPane với 2 bảng trái-phải */}
      <div style={{ marginTop: '1rem' }}>
        <ResizableSplitPane
          left={leftPanel}
          right={rightPanel}
          initialLeftWidth={420}
          minLeft={300}
          minRight={400}
          style={{ height: 500, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px #f0f1f2', padding: 16 }}
        />
      </div>

      {/* Phần dưới: Bảng saved list độc lập */}
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Danh sách học bù/học lại đã lưu ({makeups.length})</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button
                type="default"
                onClick={() => setDeletedLogModalOpen(true)}
                icon={<DeleteOutlined />}
                style={{ marginRight: 8 }}
              >
                Lịch sử xóa
              </Button>
              <span style={{ fontSize: '14px', fontWeight: 'normal' }}>Ngày:</span>
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                format="DD/MM/YYYY"
                placeholder="Chọn ngày"
                style={{ width: 150 }}
              />
            </div>
          </div>
        }
        bordered={false}
        style={{ marginTop: '1rem', marginBottom: 24, background: '#fff', border: '1px solid #e0e6ed' }}
        bodyStyle={{ padding: 12 }}
      >
        <Table
          rowKey={(record) => record.HocBuID || record.id || record.MaHS}
          dataSource={makeups}
          loading={loadingMakeups}
          columns={[
            { title: 'Mã SV', dataIndex: 'MaHS', width: 100 },
            { title: 'Họ và tên', dataIndex: 'HoTen', width: 150 },
            {
              title: 'Ngày sinh', dataIndex: 'NgaySinh', width: 120,
              render: (val) => val ? new Date(val).toLocaleDateString('vi-VN') : ''
            },
            { title: 'Lớp sinh hoạt', dataIndex: 'LopSinhHoat', width: 120 },
            { title: 'Số tiết LT', dataIndex: 'SoTietLT', width: 80 },
            { title: 'Số tiết TH', dataIndex: 'SoTietTH', width: 80 },
            { title: 'Có hóa chất', dataIndex: 'CoHoaChat', width: 100, render: val => val ? 'Có' : 'Không' },
            { title: 'Thành tiền', dataIndex: 'ThanhTien', width: 120, render: (val) => val ? val.toLocaleString('vi-VN') + 'đ' : '0đ' },
            { title: 'Trạng thái', dataIndex: 'TrangThai', width: 150 },
            { title: 'Hình thức', dataIndex: 'HinhThuc', width: 100 },
            { title: 'Ghi chú', dataIndex: 'GhiChu', width: 120 },
            {
              title: 'Xóa',
              key: 'delete',
              width: 60,
              render: (_, record) => (
                <Button
                  type="dashed"
                  icon={<DeleteOutlined />}
                  danger
                  size="small"
                  onClick={() => handleDeleteMakeup(record)}
                />
              )
            }
          ]}
          pagination={{ pageSize: 20 }}
          bordered
          size="small"
        />
      </Card>

      <Modal
        title={`Điểm danh chi tiết - ${attendanceStudent?.HoTen || ''}`}
        open={attendanceModalOpen}
        onCancel={() => setAttendanceModalOpen(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setAttendanceModalOpen(false)}>
            Đóng
          </Button>,
          <Button
            key="selectAll"
            type="primary"
            onClick={handleSelectAllAttendances}
            disabled={!attendanceDetail.length}
          >
            Chọn tất cả
          </Button>
        ]}
      >
        <div style={{ position: 'relative', minHeight: '200px' }}>
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255, 255, 255, 0.6)',
              zIndex: 1
            }}>
              <div>Đang tải dữ liệu...</div>
            </div>
          )}

          {attendanceDetail.length > 0 ? (
            <>
              <div style={{ marginBottom: 16, backgroundColor: '#f8f8f8', padding: 16, borderRadius: 4 }}>
                <h3 style={{ marginBottom: 10 }}>Thông tin điểm danh</h3>
                <Row gutter={16}>
                  <Col span={12}>
                    <p><strong>Sinh viên:</strong> {attendanceStudent?.HoTen} ({attendanceStudent?.MaHS})</p>
                    <p><strong>Lớp:</strong> {attendanceStudent?.TenLop}</p>
                  </Col>
                  <Col span={12}>
                    <p><strong>Số buổi vắng:</strong> <span style={{ color: (getAttendanceAbsentCount() || 0) > 0 ? 'red' : 'inherit' }}>{getAttendanceAbsentCount() || 0}</span></p>
                    <p><strong>Số buổi đi muộn:</strong> <span style={{ color: (getAttendanceLateCount() || 0) > 0 ? 'orange' : 'inherit' }}>{getAttendanceLateCount() || 0} buổi</span></p>
                    <p><strong>Số lần bỏ tiết:</strong> <span style={{ color: (getAttendanceSkipCount() || 0) > 0 ? 'red' : 'inherit' }}>{getAttendanceSkipCount() || 0}</span></p>
                  </Col>
                </Row>
                {attendanceDetail.length > 0 && (
                  <Row style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #ddd' }}>
                    <Col span={24}>
                      <p>
                        <strong>Tổng số buổi:</strong> {attendanceDetail.length} |
                        <strong style={{ marginLeft: 10 }}>Đã chọn:</strong> <span style={{ color: '#52c41a' }}>
                          {selectedAttendanceKeys.length}
                        </span> |
                        <strong style={{ marginLeft: 10 }}>Chưa chọn:</strong> <span style={{ color: '#1890ff' }}>
                          {attendanceDetail.length - selectedAttendanceKeys.length}
                        </span>
                      </p>
                    </Col>
                  </Row>
                )}
              </div>

              <Table
                dataSource={attendanceDetail}
                rowKey={(row, index) => `attendance_${index}`}
                pagination={false}
                bordered
                size="small"
                columns={[
                  {
                    title: '',
                    key: 'action',
                    width: 100,
                    render: (_, row, index) => {
                      const isSelected = selectedAttendanceKeys.includes(index);

                      return isSelected ? (
                        <Button
                          type="default"
                          size="small"
                          disabled
                          style={{ background: '#f6ffed', color: '#52c41a', borderColor: '#b7eb8f' }}
                        >
                          Đã chọn
                        </Button>
                      ) : (
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleAttendanceSelect(row, index)}
                        >
                          Chọn
                        </Button>
                      );
                    }
                  },
                  { title: 'Buổi số', dataIndex: 'BuoiSo', key: 'BuoiSo', width: 80 },
                  {
                    title: 'Tên bài giảng',
                    dataIndex: 'tenbg',
                    key: 'tenbg',
                    width: 140,
                    ellipsis: { showTitle: false },
                    render: text => <Tooltip title={text}>{text}</Tooltip>
                  },
                  {
                    title: 'Ngày giảng',
                    dataIndex: 'ngaygiang',
                    key: 'ngaygiang',
                    width: 120,
                    render: v => v ? new Date(v).toLocaleDateString('vi-VN') : ''
                  },
                  {
                    title: 'Giáo viên',
                    dataIndex: 'tengv',
                    key: 'tengv',
                    width: 120,
                    ellipsis: { showTitle: false },
                    render: text => <Tooltip title={text}>{text}</Tooltip>
                  },
                  { title: 'Vắng', dataIndex: 'sobuoivang', key: 'sobuoivang', width: 80, render: v => v && v > 0 ? 'Có' : '' },
                  { title: 'Số tiết LT', dataIndex: 'sotietLT', key: 'sotietLT', width: 80 },
                  { title: 'Số tiết TH', dataIndex: 'sotietTH', key: 'sotietTH', width: 80 },
                  { title: 'Đi muộn', dataIndex: 'sophutmuon', key: 'sophutmuon', width: 80 },
                  { title: 'Bỏ tiết', dataIndex: 'solanbotiet', key: 'solanbotiet', width: 80 },
                  { title: 'Có hóa chất', dataIndex: 'isTHHoaChat', key: 'isTHHoaChat', width: 80, render: v => v === 'true' ? 'Có' : '' },
                ]}
                scroll={{ x: 650, y: 150 }}
              />
            </>
          ) : (
            !loading && (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <p>Không có dữ liệu vi phạm cho sinh viên này</p>
              </div>
            )
          )}
        </div>
      </Modal>

      {/* Modal lịch sử xóa học bù */}
      <Modal
        open={deletedLogModalOpen}
        title="Lịch sử xóa học bù"
        onCancel={() => setDeletedLogModalOpen(false)}
        footer={null}
        width={1100}
      >
        <Table
          columns={[
            { title: 'Mã HS', dataIndex: 'MaHS', key: 'MaHS', width: 90 },
            { title: 'Họ tên', dataIndex: 'HoTen', key: 'HoTen', width: 160 },
            { title: 'Môn học', dataIndex: 'MonHocID', key: 'MonHocID', width: 90 },
            { title: 'Lớp HP', dataIndex: 'LopHocPhanID', key: 'LopHocPhanID', width: 90 },
            { title: 'Số tiết LT', dataIndex: 'SoTietLT', key: 'SoTietLT', width: 80 },
            { title: 'Số tiết TH', dataIndex: 'SoTietTH', key: 'SoTietTH', width: 80 },
            {
              title: 'Hóa chất', dataIndex: 'CoHoaChat', key: 'CoHoaChat', width: 70,
              render: v => v ? 'Có' : ''
            },
            {
              title: 'Thành tiền', dataIndex: 'ThanhTien', key: 'ThanhTien', width: 100,
              render: (val) => val ? val.toLocaleString('vi-VN') + 'đ' : '0đ'
            },
            { title: 'Đã đóng', dataIndex: 'DaDongTien', key: 'DaDongTien', width: 70, render: v => v ? '✓' : '' },
            { title: 'Trạng thái', dataIndex: 'TrangThai', key: 'TrangThai', width: 110 },
            { title: 'Ghi chú', dataIndex: 'GhiChu', key: 'GhiChu', width: 120 },
            {
              title: 'Xóa lúc', dataIndex: 'deletedAt', key: 'deletedAt', width: 170,
              render: v => v ? new Date(v).toLocaleString('vi-VN') : ''
            },
          ]}
          dataSource={deletedLogList}
          rowKey={row => row.id || row.HocBuID + '_' + row.deletedAt}
          size="small"
          scroll={{ x: 1200, y: 420 }}
          pagination={{ pageSize: 20 }}
          loading={deletedLogLoading}
        />
      </Modal>
    </div>
  );
};

export default StudentMakeupManagementPage;